import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

import type { LicenseType, Scope } from '../api/types';
import { useAuth } from '../auth/useAuth';

/**
 * Convenience facade over `useAuth` that exposes tenant-scoped values without
 * pulling the login/logout API surface into every consumer. Wave 2-5 should
 * prefer `useTenant()` when they only need read access to the session.
 */
export interface TenantContextValue {
  tenantId: string;
  licenseType: LicenseType;
  scopes: readonly string[];
  hasScope: (scope: Scope) => boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps): JSX.Element {
  const { session, hasScope } = useAuth();

  const value = useMemo<TenantContextValue | null>(() => {
    if (session === null) {
      return null;
    }
    return {
      tenantId: session.tenantId,
      licenseType: session.licenseType,
      scopes: session.scopes,
      hasScope,
    };
  }, [hasScope, session]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (ctx === null) {
    throw new Error('useTenant requires an authenticated session.');
  }
  return ctx;
}
